import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCartStore, type ServerPricedLine } from '@/stores/cart.store';
import api from '@/lib/api';
import { usePreviewSession } from '@/hooks/usePreviewSession';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useOfflinePricingData } from '@/hooks/useOfflinePricingData';
import { offlineFurtherTaxRate, priceOfflineCart, resolveOfflineAtl } from '@/lib/offline-pricing';
import type { TaxBreakdownLine } from '@/lib/tax';

export interface SalePreviewResponse {
  promotionDiscount: number;
  loyaltyDiscount: number;
  loyaltyPointsConsumed: number;
  netMerchandise: number;
  totalAmount: number;
  /** POS-I52: the server's own tax figures, so the till quotes what the sale will charge. */
  subtotal: number;
  totalTax: number;
  exclusiveTax: number;
  furtherTaxAmount: number;
  buyerIsOnAtl: boolean;
  taxBreakdown: TaxBreakdownLine[];
  /** POS-I127: points the sale will earn (server rule: pre-tax, pre-redemption base). */
  loyaltyPointsToEarn?: number;
  /** POS-I447: server line prices in cart order (tier and per-product override resolved). */
  lines?: ServerPricedLine[];
}

/** The server-priced slice the cart and the Payment dialog show (POS-I52). */
export interface SalePricing {
  /** Amount due before tip: goods - discounts - promotions - loyalty + tax on top (+ further tax). */
  totalAmount: number;
  totalTax: number;
  furtherTaxAmount: number;
  taxBreakdown: TaxBreakdownLine[];
  /** POS-I127: points the sale will earn; null offline (the till then estimates). */
  loyaltyPointsToEarn: number | null;
}

/**
 * Single client-side owner for the SERVER-priced sale total shown pre-payment. (T-370, POS-I52)
 *
 * Backed by `POST /sales/preview`, which runs the SAME pricing owner
 * (`SaleService.quoteSaleTotals` → `settleSaleTotals`) the committed sale uses in
 * `SaleService.createSale` — so the previewed total (GST, ATL further tax, order discount,
 * promotions, capped/quantized loyalty) equals what Complete Sale will demand. The cart and the
 * Payment dialog both show this figure; the local cart-store maths is only the fallback while the
 * preview is loading, failed or offline. It also returns the loyalty discount (pre-tax capped +
 * quantized to whole points) the checkout shows.
 *
 * Keyed on the cart + customer + requested points + buyer ATL status + location + payment method
 * (+ order discount / coupon). `placeholderData` keeps the last good figures (of the SAME
 * cart/customer session) visible while a new keystroke re-fetches, so the Total does not flash
 * between edits (T-369 pattern).
 */
export function useLoyaltyRedeemPreview(params: {
  customerId: string | null | undefined;
  redeemPoints: number;
  couponCode?: string;
  /** Buyer ATL status picked in the Payment dialog; omitted = customer/location/tenant default. */
  buyerIsOnAtl?: boolean;
  locationId?: string | null;
  /** Selected payment method, so a payment-method-tax override is priced like the sale. */
  paymentMethod?: string;
  /** False while the dialog that needs the figures is closed. Offline, the figures come from the till's own pricing (POS-I78). */
  enabled?: boolean;
}) {
  const items = useCartStore((s) => s.items);
  const orderDiscountType = useCartStore((s) => s.orderDiscountType);
  const orderDiscountValue = useCartStore((s) => s.orderDiscountValue);
  const orderDiscountAmount = useCartStore((s) => s.orderDiscountAmount);
  const cartCustomer = useCartStore((s) => s.customer);
  const isOnline = useOnlineStatus();

  const wanted = (params.enabled ?? true) && items.length > 0;
  const active = wanted && isOnline;
  const offlineData = useOfflinePricingData(wanted && !isOnline);
  const wantsRedeem = !!params.customerId && params.redeemPoints > 0;

  const payload = active
    ? {
        customerId: params.customerId || undefined,
        redeemPoints: wantsRedeem ? params.redeemPoints : undefined,
        couponCode: params.couponCode || undefined,
        discountType: orderDiscountType || undefined,
        discountValue: orderDiscountValue || undefined,
        buyerIsOnAtl: params.buyerIsOnAtl,
        locationId: params.locationId || undefined,
        paymentMethod: params.paymentMethod,
        items: items.map((i) => ({
          productId: i.product.id,
          variantId: i.variant?.id,
          quantity: i.quantity,
          discountType: i.discountType,
          discountValue: i.discountValue,
        })),
      }
    : null;

  // New session whenever the preview goes inactive (cart emptied) or the customer changes, so a
  // prior cart's/customer's figures never show on the next one (T-375).
  const session = usePreviewSession(active, params.customerId ?? '');
  const { data, isError, isFetching, isPlaceholderData } = useQuery({
    queryKey: ['loyalty-redeem-preview', session, payload],
    queryFn: () => api.post('/sales/preview', payload).then((r) => r.data as SalePreviewResponse),
    enabled: !!payload,
    staleTime: 0,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === session ? previousData : undefined,
  });

  // POS-I447: the cart shows the unit prices the sale will charge: the server's tier and per-product
  // override resolution is the only owner, the till's own repricing is only the first guess.
  const serverLines = active && data && !isError && !isPlaceholderData ? data.lines : undefined;
  useEffect(() => {
    if (serverLines) useCartStore.getState().applyServerPrices(serverLines);
  }, [serverLines]);

  // POS-I78: no connection => price the cart on the till with the same steps the server uses
  // (promotions from the cache, further tax for an unregistered buyer, tax only on money received).
  const offlinePricing: SalePricing | null = (() => {
    if (!wanted || isOnline || !offlineData.loaded) return null;
    const location = offlineData.locations.find((l) => l.id === params.locationId) ?? null;
    const tenant = offlineData.tenant as {
      atlEnabled?: boolean;
      defaultBuyerAtlStatus?: boolean;
      furtherTaxRate?: number;
      paymentMethodTaxRules?: Record<string, { rate: number; name: string; overridesProductTax: boolean }> | null;
    } | null;
    const atl = resolveOfflineAtl(location, tenant);
    const buyerIsOnAtl = params.buyerIsOnAtl ?? cartCustomer?.isOnAtl ?? atl.defaultBuyerIsOnAtl;
    const pmRules =
      location?.paymentMethodTaxEnabled && location.paymentMethodTaxRules
        ? location.paymentMethodTaxRules
        : tenant?.paymentMethodTaxRules ?? null;
    const rule = params.paymentMethod ? pmRules?.[params.paymentMethod] ?? null : null;
    const priced = priceOfflineCart({
      items,
      orderDiscountAmount,
      promotions: offlineData.promotions,
      furtherTaxRate: offlineFurtherTaxRate(atl, buyerIsOnAtl),
      pmTaxRule: rule?.overridesProductTax ? { name: rule.name, rate: rule.rate } : null,
    });
    return {
      totalAmount: priced.totalAmount,
      totalTax: priced.totalTax,
      furtherTaxAmount: priced.furtherTaxAmount,
      taxBreakdown: priced.taxBreakdown,
      loyaltyPointsToEarn: null,
    };
  })();

  const pricing: SalePricing | null = !isOnline
    ? offlinePricing
    : active && data && !isError && !isPlaceholderData
      ? {
          totalAmount: data.totalAmount,
          totalTax: data.totalTax,
          furtherTaxAmount: data.furtherTaxAmount,
          taxBreakdown: data.taxBreakdown ?? [],
          loyaltyPointsToEarn: data.loyaltyPointsToEarn ?? null,
        }
      : null;

  return {
    loyaltyDiscount: wantsRedeem ? data?.loyaltyDiscount ?? 0 : 0,
    loyaltyPointsConsumed: wantsRedeem ? data?.loyaltyPointsConsumed ?? 0 : 0,
    // Only meaningful when a redemption was actually requested — surfaced so the UI avoids
    // silently understating the Total when the check is unavailable (server still caps on commit).
    loyaltyPreviewFailed: wantsRedeem && active ? isError : false,
    // First load of a redemption (no discount to hold steady yet) — drives a "calculating" cue. (T-375)
    loyaltyPreviewPending: wantsRedeem && active && isFetching && data === undefined,
    /** Server-priced total + tax rows; null until the first answer (or when it failed/offline). */
    pricing,
  };
}
